import React, { useEffect, useState } from "react";
import Select from "react-select";
import validator from "validator";
import {
  Button,
  Card,
  Form,
  Container,
  Row,
  Col,
  Table,
  Badge,
  Alert,
} from "react-bootstrap";
import { ToastContainer } from "react-toastify";
import {
  updateSaleInvoice,
  fetchSaleInvoices,
} from "../../../../../Redux/saleInvoiceSlice";
import { useInvoiceData, notify } from "../hooks/useInvoiceData";

function UpdateDeliveryNote() {
  const {
    date,
    setDate,
    invoiceItems,
    setInvoiceItems,
    invoiceNumber,
    setInvoiceNumber,
    products,
    clients,
    drivers,
    selectedCities,
    setSelectedCities,
    client,
    setClient,
    driver,
    setDriver,
    totalHT,
    totalTTC,
    totalVAT,
    type,
    status,
    setStatus,
    shippingNoteInvoices,
    setShippingNoteInvoices,
    selectedShippingNote,
    setSelectedShippingNote,
    shippingNoteProducts,
    setShippingNoteProducts,
    loadingShippingNotes,
    setLoadingShippingNotes,
    originalShippingNoteId,
    id,
    dispatch,
    navigate,
    calculateTotals,
  } = useInvoiceData("DELIVERY_NOTE", true);

  // Get current user from localStorage
  const [currentUser, setCurrentUser] = useState(null);
  const [filteredDrivers, setFilteredDrivers] = useState([]);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const user = JSON.parse(userData);
        setCurrentUser(user);
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
  }, []);

  // Filter drivers for commercial users
  useEffect(() => {
    if (currentUser && currentUser.role === "COMMERCIAL" && currentUser.cin) {
      // Filter drivers that belong to this commercial
      const filtered = drivers.filter(
        (driver) => driver.cin === currentUser.cin,
      );
      setFilteredDrivers(filtered);

      // If only one driver, auto-select them
      if (filtered.length === 1 && !driver) {
        const singleDriver = filtered[0];
        setDriver({
          value: singleDriver.id,
          label: `${singleDriver.firstName} ${singleDriver.lastName}`,
          driver: singleDriver,
          cin: singleDriver.cin,
        });
      }
    } else {
      setFilteredDrivers(drivers);
    }
  }, [currentUser, drivers]);

  // Debug logging
  useEffect(() => {
    console.log("Current invoiceItems:", invoiceItems);
    console.log("Current shippingNoteProducts:", shippingNoteProducts);
    console.log("Current selectedShippingNote:", selectedShippingNote);
    console.log("Available products:", products);
  }, [invoiceItems, shippingNoteProducts, selectedShippingNote, products]);

  // Initialize with existing shipping note data from invoice items
  useEffect(() => {
    const initializeShippingNoteData = () => {
      // Check if any items have shippingNoteItemId
      const hasShippingNoteItems = invoiceItems.some(
        (item) => item.shippingNoteItemId,
      );

      if (hasShippingNoteItems && !selectedShippingNote) {
        // Get the shipping note ID from the first item that has it
        const firstItemWithShippingNote = invoiceItems.find(
          (item) => item.shippingNoteItemId,
        );
        const shippingNoteId = firstItemWithShippingNote?.shippingNoteId;

        if (shippingNoteId) {
          // Set the selected shipping note
          setSelectedShippingNote({
            value: shippingNoteId,
            label: `Bon sortie #${shippingNoteId}`,
          });

          // Process shipping note products from existing items
          const productsWithOriginalQty = invoiceItems
            .filter((item) => item.shippingNoteItemId)
            .map((item) => {
              // Get the original quantity - try multiple sources
              const originalQuantity =
                item.originalQuantity ||
                item.shippingNoteItem?.quantity ||
                item.quantity;

              const product = products.find((p) => p.id === item.productId);

              return {
                productId: item.productId,
                productName:
                  product?.name ||
                  item.product?.name ||
                  item.shippingNoteItem?.product?.name ||
                  `Produit ${item.productId}`,
                quantity: originalQuantity,
                price: item.price,
                vatRate: item.vatRate,
                vatAmount: item.vatAmount,
                originalQuantity: originalQuantity,
                shippingNoteItemId: item.shippingNoteItemId,
                shippingNoteId: shippingNoteId,
              };
            });

          setShippingNoteProducts(productsWithOriginalQty);

          // Update invoice items to ensure originalQuantity is set
          const updatedItems = invoiceItems.map((item) => {
            if (item.shippingNoteItemId) {
              const matchingProduct = productsWithOriginalQty.find(
                (p) => p.shippingNoteItemId === item.shippingNoteItemId,
              );

              return {
                ...item,
                originalQuantity:
                  matchingProduct?.originalQuantity ||
                  item.originalQuantity ||
                  item.quantity,
                productName:
                  item.product?.name ||
                  item.shippingNoteItem?.product?.name ||
                  products.find((p) => p.id === item.productId)?.name ||
                  `Produit ${item.productId}`,
              };
            }
            return item;
          });

          setInvoiceItems(updatedItems);

          console.log(
            "Loaded original quantities from shippingNoteItem:",
            productsWithOriginalQty.map((p) => ({
              productId: p.productId,
              productName: p.productName,
              originalQty: p.originalQuantity,
            })),
          );
        }
      }
    };

    initializeShippingNoteData();
  }, [invoiceItems, products, selectedShippingNote]);

  // Fetch shipping note invoices when driver is selected
  useEffect(() => {
    const fetchShippingNoteInvoices = async () => {
      if (driver?.value) {
        setLoadingShippingNotes(true);
        try {
          let filters = { type: "SHIPPING_NOTE_INVOICE" };

          // If user is COMMERCIAL, filter by the commercial's CIN
          if (currentUser && currentUser.role === "COMMERCIAL") {
            if (currentUser.cin) {
              filters.driverCIN = currentUser.cin;
            }
          }

          const response = await dispatch(fetchSaleInvoices(filters));
          let filteredInvoices = response.payload || [];

          // Filter invoices for this driver and exclude current invoice's shipping note if already linked
          filteredInvoices = filteredInvoices.filter(
            (inv) =>
              inv.driver?.id === driver.value &&
              (!originalShippingNoteId || inv.id !== originalShippingNoteId),
          );

          setShippingNoteInvoices(filteredInvoices);
        } catch (error) {
          console.error("Error fetching shipping notes:", error);
          notify(2, "Erreur lors du chargement des factures bon sortie");
          setShippingNoteInvoices([]);
        } finally {
          setLoadingShippingNotes(false);
        }
      } else {
        setShippingNoteInvoices([]);
        setSelectedShippingNote(null);
        setShippingNoteProducts([]);
      }
    };

    fetchShippingNoteInvoices();
  }, [
    driver,
    dispatch,
    setLoadingShippingNotes,
    setShippingNoteInvoices,
    setSelectedShippingNote,
    setShippingNoteProducts,
    originalShippingNoteId,
    currentUser,
  ]);

  useEffect(() => {
    calculateTotals();
  }, [invoiceItems, calculateTotals]);

  const handleItemChange = (index, field, value, additionalData = {}) => {
    const newItems = invoiceItems.map((item, i) => {
      if (i === index) {
        let updatedItem = { ...item, [field]: value };

        if (field === "productId" && Object.keys(additionalData).length > 0) {
          // Product selected from shipping note
          updatedItem = {
            ...updatedItem,
            price: additionalData.price || 0,
            vatRate: additionalData.vatRate || 0,
            originalQuantity: additionalData.originalQuantity || null,
            shippingNoteItemId: additionalData.shippingNoteItemId || null,
            shippingNoteId: additionalData.shippingNoteId || null,
            productName:
              additionalData.label?.split(" (Dispo:")[0] || item.productName,
          };
        } else if (
          field === "productId" &&
          Object.keys(additionalData).length === 0
        ) {
          // Product selected from regular products
          const selectedProduct = products.find((p) => p.id === value);
          if (selectedProduct) {
            updatedItem.price =
              selectedProduct.salePrice || selectedProduct.price || 0;
            updatedItem.vatRate = selectedProduct.vat || 0;
            updatedItem.originalQuantity = null;
            updatedItem.shippingNoteItemId = null;
            updatedItem.shippingNoteId = null;
            updatedItem.productName = selectedProduct.name;
          }
        }

        if (field === "quantity" && updatedItem.originalQuantity) {
          if (value > updatedItem.originalQuantity) {
            notify(
              2,
              `La quantité ne peut pas dépasser ${updatedItem.originalQuantity}`,
            );
            updatedItem.quantity = updatedItem.originalQuantity;
          }
        }

        const itemHT = (updatedItem.price || 0) * (updatedItem.quantity || 0);
        const itemVAT = itemHT * ((updatedItem.vatRate || 0) / 100);

        updatedItem.totalHT = itemHT;
        updatedItem.vatAmount = itemVAT;
        updatedItem.totalTTC = itemHT + itemVAT;

        return updatedItem;
      }
      return item;
    });

    setInvoiceItems(newItems);
  };

  const handleAddItem = () => {
    setInvoiceItems([
      ...invoiceItems,
      {
        productId: "",
        quantity: 1,
        price: 0,
        vatRate: 0,
        vatAmount: 0,
        totalHT: 0,
        totalTTC: 0,
        originalQuantity: null,
        shippingNoteItemId: null,
        shippingNoteId: null,
        productName: "",
      },
    ]);
  };

  const handleRemoveItem = (index) => {
    const newItems = invoiceItems.filter((_, i) => i !== index);
    setInvoiceItems(newItems);
  };

  const loadShippingNoteData = (shippingNote) => {
    if (
      window.confirm(
        "Voulez-vous charger les données de cette facture bon sortie ?\nAttention: Cela remplacera les articles actuels.",
      )
    ) {
      // Clear current items
      setInvoiceItems([]);

      // Update date if needed
      setDate(shippingNote.date.split("T")[0]);

      if (shippingNote.client) {
        setClient({
          value: shippingNote.client.id,
          label: shippingNote.client.name,
          client: shippingNote.client,
        });
      }

      if (shippingNote.cities && shippingNote.cities.length > 0) {
        const citiesFromNote = shippingNote.cities.map((cityRelation) => ({
          value: cityRelation.city.id,
          label: cityRelation.city.name,
          state: cityRelation.city.state,
          country: cityRelation.city.country,
        }));
        setSelectedCities(citiesFromNote);
      }

      // Process all products from shipping note
      const allProducts = shippingNote.items.map((item) => {
        const product = products.find((p) => p.id === item.productId);
        const productName =
          product?.name || item.product?.name || `Produit ${item.productId}`;

        return {
          productId: item.productId,
          productName: productName,
          quantity: item.quantity,
          price: item.price,
          vatRate: item.vatRate,
          vatAmount: item.vatAmount,
          originalQuantity: item.quantity,
          shippingNoteItemId: item.id,
          shippingNoteId: shippingNote.id,
        };
      });

      setShippingNoteProducts(allProducts);
      setSelectedShippingNote({
        value: shippingNote.id,
        label: shippingNote.invoiceNumber,
      });

      // Create initial invoice items with zero quantity
      const initialItems = allProducts.map((product) => ({
        productId: product.productId,
        productName: product.productName,
        quantity: 0,
        price: product.price,
        vatRate: product.vatRate,
        vatAmount: 0,
        totalHT: 0,
        totalTTC: 0,
        originalQuantity: product.originalQuantity,
        shippingNoteItemId: product.shippingNoteItemId,
        shippingNoteId: product.shippingNoteId,
      }));

      setInvoiceItems(initialItems);

      console.log(
        "Loaded shipping note products with original quantities:",
        allProducts.map((p) => ({
          productId: p.productId,
          productName: p.productName,
          originalQuantity: p.originalQuantity,
        })),
      );

      const availableProducts = allProducts.filter(
        (p) => p.quantity > 0,
      ).length;
      if (availableProducts > 0) {
        notify(
          1,
          `${availableProducts} produit(s) disponible(s) depuis la facture bon sortie`,
        );
      }
    }
  };

  const getProductOptions = () => {
    // If we have a selected shipping note, only show products from that shipping note
    if (selectedShippingNote && shippingNoteProducts.length > 0) {
      return shippingNoteProducts
        .filter((p) => p.quantity > 0)
        .map((p) => ({
          label: `${p.productName} (Dispo: ${p.quantity})`,
          value: p.productId,
          originalQuantity: p.quantity,
          price: p.price,
          vatRate: p.vatRate,
          shippingNoteItemId: p.shippingNoteItemId,
          shippingNoteId: p.shippingNoteId,
        }));
    }

    // Otherwise show all products
    return products.map((product) => ({
      label: product.name,
      value: product.id,
      price: product.salePrice || product.price || 0,
      vatRate: product.vat || 0,
    }));
  };

  const shouldShowOriginalQuantity = () => {
    return selectedShippingNote && shippingNoteProducts.length > 0;
  };

  const submitForm = async () => {
    if (validator.isEmpty(invoiceNumber)) {
      notify(2, "Numéro de facture est obligatoire");
      return;
    }

    if (!client) {
      notify(2, "Client est obligatoire");
      return;
    }

    // Filter out items with quantity 0
    const itemsToSubmit = invoiceItems.filter((item) => item.quantity > 0);

    if (itemsToSubmit.length === 0) {
      notify(2, "Au moins un article avec quantité > 0 est obligatoire");
      return;
    }

    for (let i = 0; i < itemsToSubmit.length; i++) {
      if (!itemsToSubmit[i].productId) {
        notify(2, `L'article ${i + 1} doit avoir un produit sélectionné`);
        return;
      }

      // Validate quantity doesn't exceed original if from shipping note
      if (
        itemsToSubmit[i].originalQuantity &&
        itemsToSubmit[i].quantity > itemsToSubmit[i].originalQuantity
      ) {
        notify(
          2,
          `La quantité de l'article ${itemsToSubmit[i].productName || i + 1} ne peut pas dépasser ${itemsToSubmit[i].originalQuantity}`,
        );
        return;
      }
    }

    const items = itemsToSubmit.map((item) => ({
      productId: item.productId,
      quantity: item.quantity,
      price: item.price,
      vatRate: item.vatRate,
      vatAmount: item.vatAmount,
      shippingNoteItemId: item.shippingNoteItemId || null,
    }));

    try {
      const invoiceData = {
        invoiceNumber,
        date,
        type: "DELIVERY_NOTE",
        status,
        items,
        totalHT,
        totalTTC,
        clientId: client.value,
      };

      if (driver) {
        invoiceData.driverId = driver.value;
      }

      // Add shipping note ID if selected
      if (selectedShippingNote) {
        invoiceData.shippingNoteId = selectedShippingNote.value;
      }

      await dispatch(updateSaleInvoice({ id, ...invoiceData }));
      notify(1, "Bon de livraison mis à jour avec succès");

      setTimeout(
        () => navigate.push("/sale-invoices/list/DELIVERY_NOTE"),
        1500,
      );
    } catch (error) {
      console.error("Update error:", error);
      notify(2, "Erreur lors de la mise à jour");
    }
  };

  const statusOptions = [
    { value: "DRAFT", label: "Brouillon" },
    { value: "VALIDATED", label: "Validée" },
    { value: "PAID", label: "Payée" },
    { value: "CANCELLED", label: "Annulée" },
  ];

  const driverOptions = (
    currentUser?.role === "COMMERCIAL" ? filteredDrivers : drivers
  ).map((driver) => ({
    label: `${driver.firstName} ${driver.lastName} ${driver.phone ? `(${driver.phone})` : ""} ${driver.cin ? `- CIN: ${driver.cin}` : ""}`,
    value: driver.id,
    driver: driver,
    cin: driver.cin,
  }));

  return (
    <Container fluid>
      <ToastContainer />
      <Row>
        <Col md="12">
          <Button
            className="btn-wd btn-outline mr-1 float-left"
            variant="info"
            onClick={() => navigate.push("/sale-invoices/list/DELIVERY_NOTE")}
          >
            <span className="btn-label">
              <i className="fas fa-list"></i>
            </span>
            Retour à la liste
          </Button>
        </Col>
      </Row>

      <Form className="form">
        <Card>
          <Card.Header>
            <Card.Title as="h4">Modifier bon de livraison</Card.Title>
            {currentUser?.role === "COMMERCIAL" && (
              <Badge bg="info" className="ml-2">
                Commercial: {currentUser.cin}
              </Badge>
            )}
          </Card.Header>

          <Card.Body>
            <Row>
              <Col md="4">
                <Form.Group>
                  <label>Numéro de facture* </label>
                  <Form.Control
                    value={invoiceNumber}
                    placeholder="Ex: BL-2023-001"
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md="4">
                <Form.Group>
                  <label>Type de facture* </label>
                  <Form.Control value="Bon de livraison" readOnly />
                </Form.Group>
              </Col>
              <Col md="4">
                <Form.Group>
                  <label>Statut </label>
                  <Select
                    value={statusOptions.find((opt) => opt.value === status)}
                    options={statusOptions}
                    onChange={(e) => setStatus(e.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md="4">
                <Form.Group>
                  <label>Date* </label>
                  <Form.Control
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col md="4">
                <Form.Group>
                  <label>Client* </label>
                  <Select
                    placeholder="Sélectionner un client"
                    value={client}
                    options={clients.map((client) => ({
                      label: client.name,
                      value: client.id,
                      client: client,
                    }))}
                    onChange={(e) => setClient(e)}
                  />
                </Form.Group>
              </Col>

              <Col md="4">
                <Form.Group>
                  <label>Chauffeur (optionnel)</label>
                  {currentUser?.role === "COMMERCIAL" ? (
                    // Show read-only input with driver's full name for commercial users
                    <Form.Control
                      type="text"
                      value={
                        driver
                          ? `${driver.driver?.firstName} ${driver.driver?.lastName}`
                          : "Aucun chauffeur sélectionné"
                      }
                      readOnly
                      disabled
                      style={{ backgroundColor: "#e9ecef" }}
                    />
                  ) : (
                    // Show select for non-commercial users
                    <Select
                      placeholder="Sélectionner un chauffeur"
                      value={driver}
                      options={driverOptions}
                      onChange={(e) => setDriver(e)}
                      isClearable
                    />
                  )}
                  <small className="text-muted">
                    {currentUser?.role === "COMMERCIAL"
                      ? "Chauffeur automatiquement associé à votre compte commercial"
                      : "Sélectionnez pour lier à une facture bon sortie"}
                  </small>
                </Form.Group>
              </Col>
            </Row>

            {/* Show current shipping note if exists */}
            {selectedShippingNote && (
              <Row className="mt-3">
                <Col md="12">
                  <Alert variant="info">
                    <i className="fas fa-info-circle mr-2"></i>
                    Facture bon sortie liée actuellement :
                    <strong> {selectedShippingNote.label}</strong>
                    {originalShippingNoteId && (
                      <Badge bg="secondary" className="ml-2">
                        ID: {originalShippingNoteId}
                      </Badge>
                    )}
                    <Badge bg="success" className="ml-2">
                      {shippingNoteProducts.length} produit(s)
                    </Badge>
                  </Alert>
                </Col>
              </Row>
            )}

            {/* Shipping Note Invoices List - Only show if driver is selected and no shipping note selected */}
            {driver?.value && !selectedShippingNote && (
              <Row className="mt-3">
                <Col md="12">
                  <Card className="bg-light">
                    <Card.Header>
                      <Card.Title as="h6">
                        <i className="fas fa-file-invoice mr-2"></i>
                        Factures bon sortie disponibles pour ce chauffeur
                        {loadingShippingNotes && (
                          <span className="ml-2">
                            <i className="fas fa-spinner fa-spin"></i>
                          </span>
                        )}
                        <Badge bg="info" className="ml-2">
                          {shippingNoteInvoices.length}
                        </Badge>
                      </Card.Title>
                    </Card.Header>
                    <Card.Body
                      style={{ maxHeight: "250px", overflowY: "auto" }}
                    >
                      {shippingNoteInvoices.length > 0 ? (
                        <Table size="sm" striped bordered hover>
                          <thead>
                            <tr>
                              <th>N° Facture</th>
                              <th>Date</th>
                              <th>Client</th>
                              <th>Chauffeur</th>
                              <th>CIN Chauffeur</th>
                              <th>Villes</th>
                              <th>Total TTC</th>
                              <th>Produits disponibles</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {shippingNoteInvoices.map((note) => {
                              const availableProducts =
                                note.items?.filter((item) => item.quantity > 0)
                                  .length || 0;

                              return (
                                <tr key={note.id}>
                                  <td>{note.invoiceNumber}</td>
                                  <td>
                                    {new Date(note.date).toLocaleDateString()}
                                  </td>
                                  <td>{note.client?.name || "N/A"}</td>
                                  <td>
                                    {note.driver
                                      ? `${note.driver.firstName} ${note.driver.lastName}`
                                      : "-"}
                                  </td>
                                  <td>
                                    <Badge bg="secondary">
                                      {note.driver?.cin || "-"}
                                    </Badge>
                                  </td>
                                  <td>
                                    <Badge bg="info">
                                      {note.cities?.length || 0} ville(s)
                                    </Badge>
                                  </td>
                                  <td className="text-right">
                                    {note.totalTTC?.toFixed(2)} TND
                                  </td>
                                  <td className="text-center">
                                    {availableProducts === 0 ? (
                                      <Badge bg="danger">0 disponible</Badge>
                                    ) : (
                                      <Badge bg="success">
                                        {availableProducts} produit(s)
                                      </Badge>
                                    )}
                                  </td>
                                  <td>
                                    <Button
                                      variant="primary"
                                      size="sm"
                                      onClick={() => loadShippingNoteData(note)}
                                      disabled={availableProducts === 0}
                                    >
                                      <i className="fas fa-download"></i>{" "}
                                      Charger
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      ) : (
                        <p className="text-muted text-center mb-0">
                          {currentUser?.role === "COMMERCIAL"
                            ? "Aucune facture bon sortie trouvée pour ce chauffeur avec votre CIN"
                            : "Aucune facture bon sortie trouvée pour ce chauffeur"}
                        </p>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            <br />

            {/* Items Table */}
            <Row>
              <Col md="12">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <Button variant="success" onClick={handleAddItem}>
                    <i className="fas fa-plus mr-2"></i>
                    Ajouter un article
                  </Button>
                  {selectedShippingNote && (
                    <div>
                      <Badge bg="secondary" className="mr-2">
                        Produits disponibles:{" "}
                        {
                          shippingNoteProducts.filter((p) => p.quantity > 0)
                            .length
                        }
                      </Badge>
                      <Badge bg="info">
                        Articles en cours:{" "}
                        {invoiceItems.filter((i) => i.quantity > 0).length}
                      </Badge>
                    </div>
                  )}
                </div>

                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Produit</th>
                      <th>Quantité</th>
                      {shouldShowOriginalQuantity() && (
                        <th>Qté originale (bon sortie)</th>
                      )}
                      <th>Prix unitaire (TND)</th>
                      <th>TVA %</th>
                      <th>Montant TVA</th>
                      <th>Total HT</th>
                      <th>Total TTC</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceItems.map((item, index) => {
                      const options = getProductOptions();
                      const productOption = options.find(
                        (p) => Number(p.value) === Number(item.productId),
                      );

                      // Debug for this specific row
                      if (item.productId && !productOption) {
                        console.log(
                          `Product ${item.productId} not found in options:`,
                          {
                            itemProductId: item.productId,
                            itemProductName: item.productName,
                            options: options,
                          },
                        );
                      }

                      return (
                        <tr key={index}>
                          <td>
                            <Select
                              placeholder="Sélectionner un produit"
                              value={productOption}
                              options={options}
                              onChange={(selectedOption) => {
                                console.log("Selected option:", selectedOption);
                                handleItemChange(
                                  index,
                                  "productId",
                                  selectedOption.value,
                                  {
                                    price: selectedOption.price,
                                    vatRate: selectedOption.vatRate,
                                    originalQuantity:
                                      selectedOption.originalQuantity,
                                    shippingNoteItemId:
                                      selectedOption.shippingNoteItemId,
                                    shippingNoteId:
                                      selectedOption.shippingNoteId,
                                    label: selectedOption.label,
                                  },
                                );
                              }}
                            />
                          </td>
                          <td>
                            <Form.Control
                              value={item.quantity || 0}
                              type="number"
                              min="0"
                              max={item.originalQuantity || undefined}
                              onChange={(e) =>
                                handleItemChange(
                                  index,
                                  "quantity",
                                  parseInt(e.target.value) || 0,
                                )
                              }
                            />
                          </td>
                          {shouldShowOriginalQuantity() && (
                            <td className="text-center align-middle">
                              {item.originalQuantity ? (
                                <div>
                                  <Badge bg="info">
                                    {item.originalQuantity}
                                  </Badge>
                                  {item.shippingNoteItemId && (
                                    <small className="d-block text-muted">
                                      ID: {item.shippingNoteItemId}
                                    </small>
                                  )}
                                </div>
                              ) : (
                                <Badge bg="secondary">-</Badge>
                              )}
                            </td>
                          )}
                          <td>
                            <Form.Control
                              value={item.price || 0}
                              type="number"
                              step="0.01"
                              min="0"
                              onChange={(e) =>
                                handleItemChange(
                                  index,
                                  "price",
                                  parseFloat(e.target.value) || 0,
                                )
                              }
                              readOnly={
                                selectedShippingNote && item.originalQuantity
                              }
                            />
                          </td>
                          <td>
                            <Form.Control
                              value={item.vatRate}
                              type="number"
                              step="0.01"
                              min="0"
                              readOnly
                            />
                          </td>
                          <td className="text-right">
                            {(item.vatAmount || 0).toFixed(2)}
                          </td>
                          <td className="text-right">
                            {(item.totalHT || 0).toFixed(2)}
                          </td>
                          <td className="text-right">
                            {(item.totalTTC || 0).toFixed(2)}
                          </td>
                          <td>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleRemoveItem(index)}
                            >
                              <i className="fas fa-trash"></i>
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Col>
            </Row>

            {/* Totals */}
            <Row className="mt-3">
              <Col md="3" className="offset-md-6">
                <Form.Group>
                  <label>Total HT</label>
                  <Form.Control
                    value={(totalHT || 0).toFixed(2)}
                    readOnly
                    className="text-right font-weight-bold"
                  />
                </Form.Group>
              </Col>
              <Col md="3">
                <Form.Group>
                  <label>Total TVA</label>
                  <Form.Control
                    value={(totalVAT || 0).toFixed(2)}
                    readOnly
                    className="text-right"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md="3" className="offset-md-9">
                <Form.Group>
                  <label>Total TTC</label>
                  <Form.Control
                    value={(totalTTC || 0).toFixed(2)}
                    readOnly
                    className="text-right font-weight-bold text-primary"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>

          <Card.Footer>
            <Button
              className="btn-wd btn-outline mr-1 float-left"
              variant="primary"
              onClick={submitForm}
            >
              <i className="fas fa-sync mr-2"></i>
              Mettre à jour
            </Button>
          </Card.Footer>
        </Card>
      </Form>
    </Container>
  );
}

export default UpdateDeliveryNote;
